const API_URL = process.env.REACT_APP_API_URL;

async function asJsonOrThrow(response, fallback) {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || body.message || fallback);
  }
  return response.json();
}

/**
 * Inviting people into an organisation, and taking an invitation up.
 *
 * An invitation is a link. `invite` returns the raw token exactly once, nothing else ever will,
 * and the browser turns it into a URL against its own origin, so the link is always for the host
 * the inviter is actually using.
 */
class InvitationService {
  /** The URL to send somebody, built from a token the create call returned. */
  linkFor(token) {
    return `${window.location.origin}/invite/${token}`;
  }

  async list(organisationId, authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/organisations/${organisationId}/invitations`);
    return asJsonOrThrow(response, 'Failed to load invitations');
  }

  async invite(organisationId, { email, roleName }, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/organisations/${organisationId}/invitations`,
      { method: 'POST', body: JSON.stringify({ email, roleName }) }
    );
    return asJsonOrThrow(response, 'Failed to send the invitation');
  }

  async revoke(organisationId, invitationId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/organisations/${organisationId}/invitations/${invitationId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || body.message || 'Failed to withdraw the invitation');
    }
  }

  /** What the link says about itself. No session needed, the invitee may not have one yet. */
  async preview(token) {
    const response = await fetch(`${API_URL}/api/invitations/${encodeURIComponent(token)}`);
    return asJsonOrThrow(response, 'This invitation link is not valid.');
  }

  async accept(token, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/invitations/${encodeURIComponent(token)}/accept`,
      { method: 'POST' }
    );
    return asJsonOrThrow(response, 'Could not accept this invitation');
  }
}

export const invitationService = new InvitationService();
export default invitationService;
